import { useEffect, useState, useCallback, useRef } from "react";
import { useLiveRev } from "@/lib/live";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import Timeline from "@/components/crm/Timeline";
import LogActivity from "@/components/crm/LogActivity";
import LeadFields from "@/components/crm/LeadFields";
import OutcomeDialog from "@/components/crm/OutcomeDialog";
import LeadEmails from "@/components/crm/LeadEmails";
import LeadCalls from "@/components/crm/LeadCalls";
import { LeadHistory, AddTaskDialog } from "@/components/crm/LeadExtras";
import { useConfirm } from "@/components/ConfirmDialog";
import { useSearchParams } from "react-router-dom";
import { Rows } from "@/components/ui-helpers";
import { StagePill } from "@/components/crm/LeadViews";
import { fmtDateTime, fmtRel, initials, dueTone, DUE_TEXT, fmtDue } from "@/lib/fmt";
import { useAuth } from "@/context/AuthContext";
import { Trash2, Video, Mail, Phone, MessageCircle, Maximize2, Minimize2, ChevronUp, ChevronDown, PhoneCall, ListPlus, Pencil, Plus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const COL = "xl:max-h-[calc(100vh-128px)] xl:overflow-y-auto no-scrollbar";
const TABS = ["timeline", "emails", "calls", "notes", "meetings", "answers", "history"];

function SopSwitch({ lead, isAdmin, onChanged }) {
  const [sops, setSops] = useState([]);
  useEffect(() => { api.get("/crm/sops").then((r) => setSops(r.data.rows)); }, []);
  const cur = sops.find((s) => s._id === lead.sop_id);
  const change = async (sop_id) => {
    try { await api.post(`/crm/sop/leads/${lead._id}/assign`, { sop_id }); toast.success("SOP changed"); onChanged(); }
    catch (e) { if (!e.response?.data?.approval_required) toast.error(e.response?.data?.detail || "Change failed"); }
  };
  if (!isAdmin && !sops.length) return <span className="pill" data-testid="drawer-sop-badge">{cur?.name || "none"}</span>;
  return (
    <select className="w-full px-2.5 py-1.5 rounded-md border text-sm" value={lead.sop_id || ""} onChange={(e) => e.target.value && change(e.target.value)} data-testid="drawer-sop-select" title="Change SOP (admin)">
      <option value="">None</option>{sops.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
    </select>
  );
}

function FormAnswers({ activity }) {
  const sub = activity.find((a) => a.kind === "form_submission");
  if (!sub) return <div className="text-sm text-[var(--text-faint)]">No form submission.</div>;
  const d = sub.data || {};
  const rows = Object.values(d.answers || {});
  return (
    <section data-testid="form-answers">
      <div className="flex items-center mb-3">
        <h4 className="section-label">Form answers · {d.form_title || d.form_slug}</h4>
        {d.outcome && d.outcome !== "neutral" && <span className={`ml-2 pill ${d.outcome === "qualified" ? "pill-ok" : "pill-breach"}`}>{d.outcome}</span>}
        <span className="ml-auto text-xs text-[var(--text-faint)]">{fmtDateTime(sub.created_at)}</span>
      </div>
      <dl className="panel-2 rounded-lg divide-y divide-[var(--border)]">
        {rows.map((x, i) => (
          <div key={i} className="grid grid-cols-1 sm:grid-cols-[minmax(0,40%)_1fr] gap-1 sm:gap-4 px-4 py-3 text-sm">
            <dt className="text-[var(--text-dim)] text-[13px]">{x.label}</dt>
            <dd className="text-[var(--text)] break-words">{x.type === "calendar_meet" ? fmtDateTime(x.value) : Array.isArray(x.value) ? x.value.join(", ") : String(x.value ?? "—")}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function GcalMeetings({ leadId, known }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/crm/leads/${leadId}/gcal`).then((r) => setRows(r.data.rows)).catch(() => setRows([])); }, [leadId]);
  if (rows === null) return <div className="text-xs text-[var(--text-faint)] mt-3">Checking Google Calendars…</div>;
  const list = rows.filter((e) => !known.has(e.id));
  if (!list.length) return null;
  return (
    <div className="mt-4" data-testid="lead-gcal-meetings">
      <h4 className="section-label mb-2">From Google Calendar</h4>
      {list.map((e) => (
        <div key={`${e.owner}-${e.id}`} className="panel-2 rounded-lg p-4 mb-2 text-[13px]" data-testid={`gcal-meeting-${e.id}`}>
          <div className="flex items-center gap-2"><Video size={14} className="text-[var(--text-dim)]" /><span className="text-sm font-medium">{fmtDateTime(e.start)}</span>
            <span className="pill ml-auto">{new Date(e.start) < new Date() ? "past" : "upcoming"}</span></div>
          <div className="mt-1">{e.title}</div>
          <div className="text-[var(--text-faint)] mt-0.5">{e.owner}</div>
          <div className="flex gap-3 mt-2">
            {e.meet_url && <a href={e.meet_url} target="_blank" rel="noreferrer" className="text-[var(--ai)] hover:underline">Join Meet</a>}
            {e.html_link && <a href={e.html_link} target="_blank" rel="noreferrer" className="text-[var(--ai)] hover:underline">See in Calendar</a>}
          </div>
        </div>
      ))}
    </div>
  );
}

function Meetings({ meetings, onChange, leadId }) {
  const set = async (m, status) => { await api.put(`/crm/meetings/${m._id}`, { status }); toast.success(`Marked ${status.replace("_", "-")}`); onChange(); };
  const known = new Set(meetings.map((m) => m.google_event_id).filter(Boolean));
  return <>
  {!meetings.length && <div className="text-sm text-[var(--text-faint)]">No meetings booked through your forms.</div>}
  {meetings.map((m) => (
    <div key={m._id} className="panel-2 rounded-lg p-4 mb-2 text-[13px]" data-testid={`meeting-${m._id}`}>
      <div className="flex items-center gap-2">
        <Video size={14} className="text-[var(--text-dim)]" /><span className="text-sm font-medium">{fmtDateTime(m.start)}</span>
        <span className={`pill ml-auto ${m.status === "held" ? "pill-ok" : m.status === "no_show" ? "pill-breach" : m.status === "pending_calendar" ? "pill-warn" : ""}`}>{m.status.replace("_", " ")}</span>
      </div>
      {m.block_type === "calendlo" && m.status !== "cancelled" && (
        <div className={`text-[11px] mt-1 ${m.confirmed_at ? "text-[var(--ok)]" : "text-[var(--warn)]"}`} data-testid={`meeting-confirm-state-${m._id}`}>
          {m.confirmed_at ? `Confirmed ${fmtDateTime(m.confirmed_at)}` : "Unconfirmed"} · {m.confirm_reminders_sent || 0} confirm reminder{m.confirm_reminders_sent === 1 ? "" : "s"} sent
        </div>
      )}
      <div className="text-neutral-500 mt-1">{m.organizer_email}</div>
      {m.meet_url && <a href={m.meet_url} target="_blank" rel="noreferrer" className="text-[var(--ai)] underline break-all">{m.meet_url}</a>}
      {m.error && <div className="text-[var(--warn)] mt-1 break-words">{m.error}</div>}
      <div className="flex gap-1 mt-2">
        {[["held", "Held"], ["no_show", "No-show"], ["cancelled", "Cancel"]].map(([k, l]) => (
          <button key={k} className="btn text-[11px] py-0.5" onClick={() => set(m, k)} data-testid={`meeting-${k}-${m._id}`}>{l}</button>
        ))}
      </div>
    </div>
  ))}
  <GcalMeetings leadId={leadId} known={known} />
  </>;
}

function Tasks({ tasks, onComplete, onEdit }) {
  const [showDone, setShowDone] = useState(false);
  const openT = tasks.filter((t) => t.status === "open");
  const done = tasks.filter((t) => t.status !== "open");
  const row = (t) => (
    <div key={t._id} className="flex items-start gap-3 py-2.5 border-b border-[var(--border)] last:border-0" data-testid={`drawer-task-${t._id}`}>
      <div className="flex-1 min-w-0"><div className={`text-sm ${t.status === "open" ? "text-[var(--text)]" : "text-[var(--text-faint)] line-through"}`}>{t.label}</div>
        <div className={`text-xs mt-0.5 ${(t.status === "open" && DUE_TEXT[dueTone(t.due)]) || "text-[var(--text-faint)]"}`}>
          {t.status === "done" ? `done · ${t.outcome}${t.note ? ` · ${t.note}` : ""}` : `due ${fmtDue(t)} · ${t.owner || "unassigned"}`}</div></div>
      <button className="btn text-xs py-1" onClick={() => onEdit(t)} data-testid={`drawer-task-edit-${t._id}`}><Pencil size={12} />Edit</button>
      {t.status === "open" && <button className="btn text-xs py-1" onClick={() => onComplete(t)} data-testid={`drawer-task-complete-${t._id}`}>Complete</button>}
    </div>
  );
  return (
    <div data-testid="drawer-tasks">
      {openT.length ? openT.map(row) : <div className="text-sm text-[var(--text-faint)] py-2">No open tasks.</div>}
      {done.length > 0 && <button className="text-xs text-[var(--text-dim)] hover:text-[var(--text)] mt-2" onClick={() => setShowDone((v) => !v)} data-testid="drawer-tasks-done-toggle">{showDone ? "Hide" : "Show"} {done.length} completed</button>}
      {showDone && done.map(row)}
    </div>
  );
}

function Card({ title, right, children, testid, className = "" }) {
  return (
    <section className={`panel rounded-lg p-4 ${className}`} data-testid={testid}>
      {title && <div className="flex items-center mb-3"><h4 className="section-label">{title}</h4><span className="ml-auto">{right}</span></div>}
      {children}
    </section>
  );
}

function Row({ label, children }) {
  return <div className="grid grid-cols-[56px_1fr] items-center gap-2"><span className="text-[13px] text-[var(--text-dim)]">{label}</span><div className="min-w-0">{children}</div></div>;
}

function MoveToPipeline({ lead, onDone }) {
  const [sops, setSops] = useState([]);
  const [sid, setSid] = useState("ad-leads");
  useEffect(() => { api.get("/crm/sops").then((r) => setSops(r.data.rows)); }, []);
  const go = async () => {
    try { await api.post(`/crm/dq/${lead._id}/move`, { sop_id: sid }); toast.success(`${lead.name} moved to pipeline`); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Move failed"); }
  };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 text-sm" data-testid="dq-move-bar">
      <span className="text-[var(--text-dim)]">{lead.module === "dq" ? "DQ lead" : "Module lead"}{lead.custom?.dq_reason ? ` · ${lead.custom.dq_reason}` : ""}</span>
      <select className="px-2.5 py-1.5 rounded-md border text-sm" value={sid} onChange={(e) => setSid(e.target.value)} data-testid={`dq-sop-${lead._id}`}>{sops.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}</select>
      <button className="btn btn-primary text-xs" onClick={go} data-testid={`dq-move-${lead._id}`}>Move to pipeline</button>
    </div>
  );
}

function NextAction({ task, onComplete, onEdit, onLogCall, onLogTask, onAddTask }) {
  const tone = task ? dueTone(task.due) : "";
  const late = tone === "late";
  return (
    <div className={`mt-4 rounded-lg border px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2 ${late ? "border-[color-mix(in_srgb,var(--breach)_50%,transparent)] bg-[color-mix(in_srgb,var(--breach)_9%,transparent)]" : "border-[color-mix(in_srgb,var(--ai)_40%,transparent)] bg-[color-mix(in_srgb,var(--ai)_8%,transparent)]"}`} data-testid="lead-next-action-card">
      <span className={`section-label ${late ? "!text-[var(--breach)]" : "!text-[var(--ai)]"}`}>Next action</span>
      <div className="flex-1 min-w-[200px]">
        {task ? (
          <div className="flex flex-wrap items-baseline gap-x-3" data-testid="drawer-next-up">
            <button className="text-base font-semibold hover:underline text-left" onClick={onEdit} title="Edit task" data-testid="drawer-next-label">{task.label}</button>
            <span className={`text-sm ${DUE_TEXT[tone] || "text-[var(--text-dim)]"}`} data-testid="drawer-next-due">{task.due ? `${late ? "Overdue · " : "Due "}${fmtDue(task)}` : "No due date"}</span>
            {task.owner && <span className="text-xs text-[var(--text-faint)]">{task.owner}</span>}
          </div>
        ) : <span className="text-sm text-[var(--text-dim)]">Nothing scheduled.</span>}
      </div>
      <div className="flex gap-2">
        {task && <button className="btn btn-primary text-xs" onClick={onComplete} data-testid="drawer-next-complete">Complete</button>}
        {task && <button className="btn text-xs" onClick={onEdit} data-testid="drawer-next-edit"><Pencil size={13} />Edit</button>}
        <button className="btn text-xs" onClick={onLogCall} data-testid="drawer-log-call"><PhoneCall size={13} />Log call</button>
        <button className="btn text-xs" onClick={onLogTask} data-testid="drawer-log-task"><CheckCircle2 size={13} />Log done task</button>
        <button className="btn text-xs" onClick={onAddTask} data-testid="drawer-add-task"><ListPlus size={13} />Add task</button>
      </div>
    </div>
  );
}

function Duplicates({ rows }) {
  const [, setParams] = useSearchParams();
  if (!rows?.length) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 text-xs" data-testid="drawer-duplicates">
      <span className="px-2 py-0.5 rounded-full border border-[var(--warn,#f59e0b)] text-[var(--warn,#f59e0b)]">Submitted {rows.length + 1}×</span>
      {rows.map((r) => (
        <button key={r._id} type="button" onClick={() => setParams({ lead: r._id })} title={`${r.name} · ${r.stage || ""}${r.module ? ` · ${r.module}` : ""}`}
          className="underline decoration-dotted text-[var(--text-dim)] hover:text-[var(--text)]" data-testid={`drawer-dup-${r._id}`}>
          {fmtRel(r.created_at)} · {r.stage || "—"}{r.module ? ` (${r.module})` : ""}
        </button>
      ))}
    </span>
  );
}

export default function LeadDrawer({ leadId, cfg, users, onClose, onChanged, onPrev, onNext }) {
  const { user } = useAuth();
  const { confirm } = useConfirm();
  const [d, setD] = useState(null);
  const [task, setTask] = useState(null);
  const [tab, setTab] = useState("timeline");
  const [logKind, setLogKind] = useState("note");
  const [addTask, setAddTask] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [logTask, setLogTask] = useState(false);
  const [rk, setRk] = useState(0);
  const [wide, setWide] = useState(localStorage.getItem("crm_drawer_wide") === "1");
  const load = useCallback(() => api.get(`/crm/leads/${leadId}`).then((r) => setD(r.data)).catch(() => { toast.error("Lead not found"); onClose(); }), [leadId, onClose]);
  useEffect(() => { setD(null); if (leadId) { load(); } }, [leadId, load]);
  useLiveRev(() => { if (leadId) load(); });
  const prompted = useRef(null);
  useEffect(() => {
    if (!d || prompted.current === leadId) return;
    prompted.current = leadId;
    const due = d.tasks.find((t) => t.status === "open" && t.sop && t.track !== "qualify" && t.due <= new Date().toISOString());
    if (due) setTask(due);
  }, [d, leadId]);
  useEffect(() => { if (!leadId) prompted.current = null; }, [leadId]);
  useEffect(() => {
    if (!leadId) return undefined;
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName) || e.metaKey || e.ctrlKey) return;
      if ((e.key === "ArrowDown" || e.key === "j") && onNext) { e.preventDefault(); onNext(); }
      if ((e.key === "ArrowUp" || e.key === "k") && onPrev) { e.preventDefault(); onPrev(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [leadId, onNext, onPrev]);
  const refresh = () => { load(); onChanged(); setRk((k) => k + 1); };
  const patch = async (body) => {
    try { await api.put(`/crm/leads/${leadId}`, body); }
    catch (e) { if (!e.response?.data?.approval_required) toast.error(e.response?.data?.detail || "Couldn't save"); }
    refresh();
  };
  const del = async () => {
    if (!(await confirm({ title: "Delete this lead?", body: "Its tasks, meetings and timeline are deleted too.", danger: true }))) return;
    try { await api.delete(`/crm/leads/${leadId}`); toast.success("Lead deleted"); onChanged(); onClose(); }
    catch (e) { if (!e.response?.data?.approval_required) toast.error(e.response?.data?.detail || "Couldn't delete"); }
  };
  const toggleWide = () => { setWide((w) => { localStorage.setItem("crm_drawer_wide", w ? "0" : "1"); return !w; }); };
  const lead = d?.lead;
  const colors = cfg.stage_colors || {};
  const open = (d?.tasks || []).filter((t) => t.status === "open").sort((a, b) => (!a.due) - (!b.due) || String(a.due).localeCompare(String(b.due)));
  const owner = users.find((u) => u.email === lead?.owner);
  const wa = lead?.phone ? `https://wa.me/${lead.phone.replace(/\D/g, "")}` : null;

  return (
    <Sheet open={!!leadId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className={`w-full ${wide ? "sm:max-w-none" : "sm:max-w-[94vw] 2xl:max-w-[1560px]"} bg-[var(--bg)] border-[var(--border-strong)] p-0 overflow-y-auto no-scrollbar transition-[max-width]`} data-testid="lead-drawer">
        {!lead ? <div className="p-6"><SheetTitle className="sr-only">Lead</SheetTitle><Rows n={6} /></div> : (
          <div>
            <div className="px-5 md:px-7 pt-4 pb-4 border-b border-[var(--border)] bg-[var(--panel)]">
              <div className="flex items-start gap-4 pr-8">
                <div className="w-12 h-12 rounded-full bg-[var(--panel-2)] border border-[var(--border-strong)] flex items-center justify-center text-base font-semibold shrink-0">{initials(lead.name)}</div>
                <div className="flex-1 min-w-0">
                  <SheetTitle className="text-2xl font-semibold tracking-tight break-words" data-testid="drawer-lead-name">{lead.name}</SheetTitle>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-2">
                    <span data-testid="drawer-stage-big"><StagePill stage={lead.stage || "No stage"} colors={colors} size="lg" /></span>
                    <span className="text-sm text-[var(--text-dim)]">{owner?.name || lead.owner || "Unassigned"}</span>
                    <span className="text-sm text-[var(--text-faint)]">{lead.source} · {fmtRel(lead.created_at)}</span>
                    <Duplicates rows={d.duplicates} />
                  </div>
                  {(lead.phone || lead.email) && (
                    <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="drawer-contact">
                      {lead.phone && <a className="btn text-xs" href={`tel:${lead.phone}`} data-testid="drawer-call"><Phone size={13} />{lead.phone}</a>}
                      {wa && <a className="btn text-xs" href={wa} target="_blank" rel="noreferrer" data-testid="drawer-whatsapp"><MessageCircle size={13} className="text-[var(--ok)]" />WhatsApp</a>}
                      {lead.email && <a className="btn text-xs" href={`mailto:${lead.email}`} data-testid="drawer-email"><Mail size={13} /><span className="break-all">{lead.email}</span></a>}
                    </div>
                  )}
                </div>
                <span className="hidden sm:inline-flex items-center">
                  <button className="btn-ghost btn p-1.5" disabled={!onPrev} onClick={onPrev} title="Previous lead (↑ / k)" data-testid="drawer-prev-btn"><ChevronUp size={14} /></button>
                  <button className="btn-ghost btn p-1.5" disabled={!onNext} onClick={onNext} title="Next lead (↓ / j)" data-testid="drawer-next-btn"><ChevronDown size={14} /></button>
                </span>
                <button className="btn-ghost btn p-1.5 hidden sm:inline-flex" onClick={toggleWide} title={wide ? "Collapse" : "Expand"} data-testid="drawer-expand-btn">{wide ? <Minimize2 size={14} /> : <Maximize2 size={14} />}</button>
              </div>
              <NextAction task={open[0]} onComplete={() => setTask(open[0])} onEdit={() => setEditTask(open[0])} onLogTask={() => setLogTask(true)} onLogCall={() => { setLogKind("call"); setTab("notes"); }} onAddTask={() => setAddTask(true)} />
              {lead.module && <MoveToPipeline lead={lead} onDone={() => { onChanged(); onClose(); }} />}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] xl:grid-cols-[340px_minmax(0,1fr)_300px] 2xl:grid-cols-[400px_minmax(0,1fr)_320px] gap-4 p-4 md:p-5 pb-12 xl:pb-4 items-start">
              <aside className={`space-y-3 order-1 xl:order-2 ${COL}`} data-testid="drawer-left">
                <section className="panel rounded-lg p-3" data-testid="drawer-pipeline">
                  <div className="grid grid-cols-1 2xl:grid-cols-3 gap-2">
                    <Row label="Stage"><select className="w-full px-2.5 py-1.5 rounded-md border text-sm" value={lead.stage || ""} onChange={(e) => patch({ stage: e.target.value })} data-testid="drawer-stage-select">{cfg.stages.map((s) => <option key={s}>{s}</option>)}</select></Row>
                    <Row label="Owner"><select className="w-full px-2.5 py-1.5 rounded-md border text-sm" value={lead.owner || ""} onChange={(e) => patch({ owner: e.target.value })} data-testid="drawer-owner-select"><option value="">Unassigned</option>{users.map((u) => <option key={u.email} value={u.email}>{u.name || u.email}</option>)}</select></Row>
                    <Row label="SOP"><SopSwitch lead={lead} isAdmin={user?.role === "admin"} onChanged={refresh} /></Row>
                  </div>
                  {!lead.sop_id && <div className="text-xs text-[var(--text-faint)] mt-2">Not on an SOP, so the stage only changes by hand.</div>}
                </section>
                <Card testid="drawer-details" className="!p-4">
                  <LeadFields wide lead={lead} fields={(cfg.fields || []).filter((f) => !["stage", "owner"].includes(f.key))} stages={cfg.stages} users={users} onSaved={refresh} />
                </Card>
                {lead.utm && Object.keys(lead.utm).length > 0 && (
                  <details className="panel rounded-lg p-3 group" data-testid="drawer-utm">
                    <summary className="section-label cursor-pointer select-none hover:text-[var(--text)]" data-testid="drawer-utm-toggle">Tracking</summary>
                    <dl className="space-y-1 mt-2">
                      {Object.entries(lead.utm).filter(([k]) => k !== "page_url").map(([k, v]) => (
                        <div key={k} className="grid grid-cols-[100px_1fr] gap-3"><dt className="text-[13px] text-[var(--text-dim)]">{k}</dt><dd className="text-sm break-all">{v}</dd></div>
                      ))}
                    </dl>
                  </details>
                )}
                {user?.role !== "viewer" && <button className="btn-ghost btn text-xs hover:text-[var(--breach)]" onClick={del} data-testid="drawer-delete-lead"><Trash2 size={13} />{user?.role === "admin" ? "Delete lead" : "Request delete"}</button>}
              </aside>
              <main className={`panel rounded-lg min-w-0 order-3 lg:col-span-2 xl:col-span-1 xl:order-1 ${COL}`} data-testid="drawer-centre">
                <div className="flex flex-wrap px-2 border-b border-[var(--border)] text-[13px]">
                  {TABS.map((t) => {
                    const n = t === "meetings" ? d.meetings.length : null;
                    return (
                      <button key={t} onClick={() => setTab(t)} data-testid={`drawer-tab-${t}`}
                        className={`px-2.5 py-2.5 capitalize whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t ? "border-[var(--tab-line)] text-[var(--text)] font-medium" : "border-transparent text-[var(--text-dim)] hover:text-[var(--text)]"}`}>{t === "answers" ? "Form answers" : t}{n ? ` · ${n}` : ""}</button>
                    );
                  })}
                </div>
                <div className="p-5">
                  {tab === "emails" && <LeadEmails lead={lead} onSent={refresh} />}
                  {tab === "calls" && <LeadCalls leadId={leadId} />}
                  {tab === "timeline" && <div className="space-y-6"><LogActivity leadId={leadId} onLogged={refresh} /><Timeline activity={d.activity} emails={d.emails} onChanged={refresh} /></div>}
                  {tab === "meetings" && <Meetings meetings={d.meetings} onChange={refresh} leadId={leadId} />}
                  {tab === "answers" && <FormAnswers activity={d.activity} />}
                  {tab === "notes" && <div className="space-y-6"><LogActivity key={logKind} initialKind={logKind} leadId={leadId} onLogged={refresh} /><Timeline activity={d.activity.filter((a) => ["note", "call", "message"].includes(a.kind))} emails={[]} /></div>}
                  {tab === "history" && <LeadHistory leadId={leadId} refreshKey={rk} onChanged={refresh} />}
                </div>
              </main>
              <aside className={`space-y-3 order-2 xl:order-3 ${COL}`} data-testid="drawer-right">
                <Card title={`Tasks · ${open.length} open`} testid="drawer-tasks-card" right={<button className="text-xs text-[var(--ai)] hover:underline inline-flex items-center gap-1" onClick={() => setAddTask(true)} data-testid="drawer-tasks-add"><Plus size={12} />Add</button>}>
                  <Tasks tasks={d.tasks} onComplete={setTask} onEdit={setEditTask} />
                </Card>
                {d.meetings[0] && (
                  <Card title="Latest meeting" testid="drawer-latest-meeting" right={<button className="text-xs text-[var(--ai)] hover:underline" onClick={() => setTab("meetings")} data-testid="drawer-see-meetings">All</button>}>
                    <div className="text-sm font-medium">{fmtDateTime(d.meetings[0].start)}</div>
                    <div className="text-xs text-[var(--text-dim)] mt-0.5 capitalize">{d.meetings[0].status.replace("_", " ")}</div>
                    {d.meetings[0].meet_url && <a href={d.meetings[0].meet_url} target="_blank" rel="noreferrer" className="btn text-xs w-full justify-center mt-3" data-testid="drawer-join-meet"><Video size={13} />Join Meet</a>}
                  </Card>
                )}
              </aside>
            </div>
          </div>
        )}
        {addTask && lead && <AddTaskDialog lead={lead} users={users} onClose={() => setAddTask(false)} onDone={() => { setAddTask(false); refresh(); }} />}
        {logTask && lead && <AddTaskDialog lead={lead} logDone users={users} onClose={() => setLogTask(false)} onDone={() => { setLogTask(false); refresh(); }} />}
        {editTask && lead && <AddTaskDialog lead={lead} task={editTask} users={users} onClose={() => setEditTask(null)} onDone={() => { setEditTask(null); refresh(); }} />}
        {task && <OutcomeDialog task={{ ...task, lead_name: lead?.name }} cadence={cfg.cadence} onClose={() => setTask(null)} onDone={() => { setTask(null); refresh(); }} />}
      </SheetContent>
    </Sheet>
  );
}
